import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { LoginRequest, LoginResponse } from '@teraseya/api-types';
import { ApiError, apiFetch } from '../lib/api-client';
import { setToken } from '../lib/auth';
import { Button, Card, Field, Spinner } from '../components/ds';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const mutation = useMutation<LoginResponse, ApiError, LoginRequest>({
    mutationFn: (input) =>
      apiFetch<LoginResponse>('/private/auth/login', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (res) => {
      setToken(res.authToken);
      void navigate({ to: '/' });
    },
  });

  const trimmedEmail = email.trim();
  const trimmedPassword = password.trim();
  const canSubmit =
    trimmedEmail.length > 0 && trimmedPassword.length > 0 && !mutation.isPending;

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    mutation.mutate({ email: trimmedEmail, password });
  };

  // Form-level error — rendered as a sibling to the fields so it summarises
  // the overall login attempt. Per-field validation isn't a thing here; the
  // backend returns one of two codes and we map them to a single message.
  const errorMessage = mutation.error
    ? mutation.error.code === 'wrong-credentials'
      ? t('login.errors.wrongCreds')
      : t('login.errors.network')
    : null;

  const supportEmail = t('login.forgot.email');

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-muted px-4">
      <div className="w-full max-w-[420px]">
        <Card.Root>
          <form onSubmit={onSubmit} noValidate>
            <Card.Header>
              <Card.Title>{t('login.title')}</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="space-y-5">
                <Field.Root>
                  <Field.Label>{t('login.email.label')}</Field.Label>
                  <Field.Input
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    disabled={mutation.isPending}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login.email.placeholder')}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('login.password.label')}</Field.Label>
                  <Field.Input
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    disabled={mutation.isPending}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('login.password.placeholder')}
                  />
                </Field.Root>

                {errorMessage && (
                  <p
                    role="alert"
                    aria-live="polite"
                    className="text-sm text-danger"
                  >
                    {errorMessage}
                  </p>
                )}

                <p className="text-sm text-text-muted">
                  <span>{t('login.forgot.text')} </span>
                  <a
                    href={`mailto:${supportEmail}`}
                    className="text-link hover:underline"
                  >
                    {supportEmail}
                  </a>
                </p>
              </div>
            </Card.Body>
            <Card.Footer className="justify-stretch">
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={!canSubmit}
                className="w-full"
              >
                {mutation.isPending && <Spinner />}
                <span>{t('login.submit')}</span>
              </Button>
            </Card.Footer>
          </form>
        </Card.Root>
      </div>
    </div>
  );
}
